<template>
	<div :class="rootClasses" :style="wrapperStyle">
		<div v-if="showDesktopControls" :class="bemm('controls')">
			<ButtonGroup>
				<Button
					data-test="advanced-filters-mode-toggle"
					:icon="isSidebarMode ? Icons.SPLIT_TOP : Icons.SPLIT_RIGHT"
					:variant="ButtonVariant.GHOST"
					:tooltip="modeToggleLabel"
					@click="toggleDesktopMode"
				/>
				<Button
					v-if="isSidebarMode"
					data-test="advanced-filters-sidebar-toggle"
					:icon="isSidebarOpen ? Icons.CHEVRON_RIGHT : Icons.CHEVRON_LEFT"
					:variant="ButtonVariant.OUTLINE"
					@click="toggleSidebarVisibility"
				>
					{{ isSidebarOpen ? sidebarHideLabel : sidebarShowLabel }}
				</Button>
			</ButtonGroup>
		</div>
		<div :class="bemm('wrapper')">
			<aside
				v-if="isSidebarMode"
				:class="bemm('column', ['sidebar', isSidebarOpen ? 'open' : 'closed'])"
			>
				<div :class="bemm('sidebar-content')">
					<slot name="filters"></slot>
				</div>
			</aside>

			<div :class="bemm('column', 'main')">
				<div v-if="!isSidebarMode" :class="bemm('collapse')">
					<Collapse v-model="localCollapseState">
						<CollapseItem
							:title="collapseTitle"
							:name="collapseName"
							:icon="collapseIcon"
							:color="collapseColor"
							:class="
								bemm(
									'collapse-item',
									hasActiveFilters ? 'has-active-filters' : ''
								)
							"
						>
							<template #title>
								<span :class="bemm('collapse-title')">
									<Icon
										v-if="collapseIcon"
										:name="collapseIcon"
										:class="bemm('collapse-title-icon')"
									/>
									{{ collapseTitle }}
								</span>
							</template>
							<template v-if="hasActiveFilters" #middle>
								<div :class="bemm('collapse-middle')">
									<span :class="bemm('active-filters')">
										<Badge
											v-for="filter in activeFilters"
											:key="filter.key"
											:class="bemm('active-filter-badge')"
											color="default"
											:value="filter.label"
										>
											{{ filter.label }}
										</Badge>
									</span>
									<Button
										data-test="advanced-filters-reset"
										:variant="ButtonVariant.GHOST"
										@click="emit('resetFilters')"
									>
										{{ resetFiltersLabel }}
									</Button>
								</div>
							</template>
							<slot name="filters"></slot>
						</CollapseItem>
					</Collapse>
				</div>

				<div :class="bemm('content')">
					<slot></slot>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useBemm } from 'bemm';
import { Button, ButtonGroup, ButtonVariant } from '../Button';
import { Collapse, CollapseItem } from '../Collapse';
import { Badge } from '../Display';
import { Icon } from '../Icon';
import { Icons } from '../Icon';
import { usePreferencesStore } from '../preferences';
import { isAdvancedFiltersCollapseOpen } from './advancedFiltersCollapse.utils';
import type {
	AdvancedFilterLayoutMode,
	AdvancedFiltersCollapseState,
	AdvancedFiltersLayoutActiveFilter,
	AdvancedFiltersLayoutProps,
} from './AdvancedFiltersLayout.model';

const props = withDefaults(defineProps<AdvancedFiltersLayoutProps>(), {
	collapseState: () => [] as AdvancedFiltersCollapseState,
	activeFilters: () => [] as AdvancedFiltersLayoutActiveFilter[],
	forceCollapse: false,
	desktopBreakpoint: 1200,
	defaultDesktopMode: 'sidebar',
	collapseName: 'filter',
	collapseIcon: Icons.SEARCH_ALT,
	sidebarWidth: 375,
	sidebarShowLabel: 'Show filters',
	sidebarHideLabel: 'Hide filters',
	sidebarModeLabel: 'Sidebar view',
	collapseModeLabel: 'Top to bottom view',
	resetFiltersLabel: 'Reset filters',
});

const emit = defineEmits<{
	(event: 'update:collapseState', value: AdvancedFiltersCollapseState): void;
	(event: 'resetFilters'): void;
}>();

const bemm = useBemm('ar-advanced-filters-layout', {
	return: 'string',
	includeBaseClass: true,
});

const settingsStore = usePreferencesStore();

const viewportWidth = ref<number>(
	typeof window === 'undefined' ? props.desktopBreakpoint : window.innerWidth
);
const desktopMode = ref<AdvancedFilterLayoutMode>(props.defaultDesktopMode);
const isSidebarOpen = ref(true);

const isDesktop = computed(
	() => Number(viewportWidth.value) >= Number(props.desktopBreakpoint)
);

const effectiveMode = computed<AdvancedFilterLayoutMode>(() =>
	props.forceCollapse || !isDesktop.value ? 'collapse' : desktopMode.value
);

const isSidebarMode = computed(() => effectiveMode.value === 'sidebar');
const showDesktopControls = computed(
	() => isDesktop.value && !props.forceCollapse
);
const collapseStateSettingsKey = computed(
	() => `${props.settingsKey}:collapse`
);
const sidebarStateSettingsKey = computed(() => `${props.settingsKey}:sidebar`);

const localCollapseState = computed({
	get: () => props.collapseState,
	set: (value: AdvancedFiltersCollapseState) => {
		emit('update:collapseState', Array.isArray(value) ? value : []);
	},
});

const modeToggleLabel = computed(() =>
	isSidebarMode.value ? props.collapseModeLabel : props.sidebarModeLabel
);

const wrapperStyle = computed(() => ({
	'--advanced-filters-sidebar-width':
		typeof props.sidebarWidth === 'number'
			? `${props.sidebarWidth}px`
			: props.sidebarWidth,
}));

const rootClasses = computed(() =>
	bemm('', [
		`mode-${effectiveMode.value}`,
		isSidebarOpen.value ? 'sidebar-open' : 'sidebar-closed',
	])
);

const hasActiveFilters = computed(
	() => Array.isArray(props.activeFilters) && props.activeFilters.length > 0
);

const updateViewportWidth = () => {
	if (typeof window === 'undefined') {
		return;
	}

	viewportWidth.value = window.innerWidth;
};

const syncDesktopModeFromSettings = () => {
	const savedMode = settingsStore.getAdvancedFilterLayoutMode(
		props.settingsKey
	);
	const savedSidebarOpenState = settingsStore.getAdvancedFilterCollapseOpen(
		sidebarStateSettingsKey.value
	);
	if (savedMode) {
		desktopMode.value = savedMode;
		if (typeof savedSidebarOpenState === 'boolean') {
			isSidebarOpen.value = savedSidebarOpenState;
		}
		return;
	}

	desktopMode.value = props.defaultDesktopMode;
	if (typeof savedSidebarOpenState === 'boolean') {
		isSidebarOpen.value = savedSidebarOpenState;
	}
	settingsStore.setAdvancedFilterLayoutMode(
		props.settingsKey,
		props.defaultDesktopMode
	);
};

const toggleSidebarVisibility = () => {
	if (!isSidebarMode.value) {
		return;
	}

	isSidebarOpen.value = !isSidebarOpen.value;
	settingsStore.setAdvancedFilterCollapseOpen(
		sidebarStateSettingsKey.value,
		isSidebarOpen.value
	);
};

const toggleDesktopMode = () => {
	if (props.forceCollapse) {
		return;
	}

	desktopMode.value = desktopMode.value === 'sidebar' ? 'collapse' : 'sidebar';

	if (desktopMode.value === 'sidebar') {
		const savedFilterOpenState = settingsStore.getAdvancedFilterCollapseOpen(
			sidebarStateSettingsKey.value
		);
		isSidebarOpen.value =
			typeof savedFilterOpenState === 'boolean' ? savedFilterOpenState : true;
	}
};

watch(
	() => props.settingsKey,
	() => {
		syncDesktopModeFromSettings();
	},
	{ immediate: true }
);

watch(desktopMode, (mode) => {
	settingsStore.setAdvancedFilterLayoutMode(props.settingsKey, mode);
});

watch(isDesktop, (nextIsDesktop) => {
	if (!nextIsDesktop) {
		isSidebarOpen.value = true;
	}
});

watch(
	[effectiveMode, () => props.collapseState, collapseStateSettingsKey],
	([mode, collapseState, settingsKey]) => {
		if (mode !== 'collapse') {
			return;
		}

		settingsStore.setAdvancedFilterCollapseOpen(
			settingsKey,
			isAdvancedFiltersCollapseOpen(collapseState)
		);
	},
	{
		deep: true,
		immediate: true,
	}
);

onMounted(() => {
	updateViewportWidth();

	if (typeof window !== 'undefined') {
		window.addEventListener('resize', updateViewportWidth, { passive: true });
	}
});

onBeforeUnmount(() => {
	if (typeof window !== 'undefined') {
		window.removeEventListener('resize', updateViewportWidth);
	}
});
</script>

<style lang="scss">
.ar-advanced-filters-layout {
	--advanced-filters-sidebar-width: 375px;

	width: 100%;

	&__wrapper {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: var(--space);
		width: 100%;
		transition: grid-template-columns 220ms ease;
	}

	&__column {
		position: relative;
		width: 100%;

		&--main {
			min-width: 0;
		}

		&--sidebar {
			position: sticky;
			top: var(--space-l);
			align-self: start;
			max-height: 100vh;
			overflow: hidden;
			opacity: 1;
			transition:
				opacity 0.2s ease,
				width 0.2s ease;
			transform-origin: right center;
		}
	}

	&__sidebar-content {
		width: var(--advanced-filters-sidebar-width);
		max-height: 100vh;
		overflow-y: auto;
		overflow-x: hidden;
		opacity: 1;
		padding-left: var(--space-l);
		font-size: 0.875em;
		transform: translateX(0);
		transition:
			opacity 220ms ease,
			transform 220ms ease;
	}

	&__controls {
		display: flex;
		justify-content: flex-end;
		margin-bottom: var(--space-s);
	}

	&__collapse {
		margin-bottom: var(--space);
	}

	&__collapse-title {
		display: inline-flex;
		align-items: center;
		gap: var(--space-xs);
		min-width: 0;
		font-weight: var(--font-weight-bold);
	}

	&__collapse-title-icon {
		opacity: 0.5;
	}

	&__collapse-middle {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: var(--space-s);
		min-width: 0;
		overflow: hidden;
	}

	&__active-filters {
		display: flex;
		align-items: center;
		gap: var(--space-s);
		min-width: 0;
		justify-content: flex-end;
		overflow: hidden;
		white-space: nowrap;
		color: color-mix(
			in srgb,
			var(--color-foreground),
			var(--color-primary) 20%
		);
	}

	&__active-filter-badge {
		min-width: 0;
		flex: 0 1 auto;
		overflow: hidden;
	}

	&--mode-sidebar {
		.ar-advanced-filters-layout__wrapper {
			grid-template-columns: minmax(0, 1fr) var(
					--advanced-filters-sidebar-width
				);
			grid-template-areas: 'main sidebar';
		}

		.ar-advanced-filters-layout__column--main {
			grid-area: main;
		}

		.ar-advanced-filters-layout__column--sidebar {
			grid-area: sidebar;
		}
	}

	&--mode-sidebar.ar-advanced-filters-layout--sidebar-closed {
		.ar-advanced-filters-layout__wrapper {
			grid-template-columns: minmax(0, 1fr) 0;
		}

		.ar-advanced-filters-layout__column--sidebar {
			opacity: 0;
			pointer-events: none;
		}

		.ar-advanced-filters-layout__sidebar-content {
			opacity: 0;
			transform: translateX(18px);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		&__wrapper,
		&__column--sidebar,
		&__sidebar-content {
			transition: none;
		}
	}
}
</style>
